import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { formatDuration, formatCurrency } from '../../utils/labour';
import { formatDate } from '../../utils/time';
import { calcBenchmarkComparison } from '../../utils/benchmark';
import './BenchmarkCard.css';

// ── Helpers ────────────────────────────────────────────────────────────

function metricBasis(benchmark, t) {
  switch (benchmark.primaryMetric) {
    case 'costPerUnit': return t('benchmark.basisCostPerUnit');
    case 'labourCost':  return t('benchmark.basisLabourCost');
    default:            return t('benchmark.basisDuration');
  }
}

function primaryLabel(benchmark, t) {
  switch (benchmark.primaryMetric) {
    case 'costPerUnit': return t('benchmark.bestCostPerUnit');
    case 'labourCost':  return t('benchmark.lowestLabourCost');
    default:            return t('benchmark.fastestCompletion');
  }
}

function primaryValue(benchmark, currency) {
  switch (benchmark.primaryMetric) {
    case 'costPerUnit':
      return formatCurrency(benchmark.costPerUnit, benchmark.labourCostCurrency || currency);
    case 'labourCost':
      return formatCurrency(benchmark.labourCost, benchmark.labourCostCurrency || currency);
    default:
      return formatDuration(benchmark.netDurationMinutes ?? benchmark.durationMinutes);
  }
}

function buildAdminMetrics(benchmark, t, currency) {
  return [
    {
      key: 'primary',
      label: primaryLabel(benchmark, t),
      value: primaryValue(benchmark, currency),
    },
    benchmark.unitsPerHour != null && {
      key: 'unitsPerHour',
      label: t('benchmark.unitsPerHour'),
      value: benchmark.unitsPerHour.toFixed(1),
    },
    benchmark.avgDurationMinutes != null && {
      key: 'avgDuration',
      label: t('benchmark.avgAllRuns'),
      value: formatDuration(benchmark.avgDurationMinutes),
    },
  ].filter(Boolean);
}

function buildProductionMetrics(benchmark, t) {
  return [
    {
      key: 'fastest',
      label: t('benchmark.fastestCompletion'),
      value: formatDuration(benchmark.netDurationMinutes ?? benchmark.durationMinutes),
    },
    benchmark.unitsPerHour != null && {
      key: 'unitsPerHour',
      label: t('benchmark.unitsPerHour'),
      value: benchmark.unitsPerHour.toFixed(1),
    },
    benchmark.avgDurationMinutes != null && {
      key: 'avgDuration',
      label: t('benchmark.avgAllRuns'),
      value: formatDuration(benchmark.avgDurationMinutes),
    },
  ].filter(Boolean);
}

// ── Comparison chip ────────────────────────────────────────────────────

function ComparisonChip({ comparison }) {
  const { t } = useTranslation();
  if (!comparison) return null;
  const { pctDiff, betterWhenLower } = comparison;
  const abs = Math.abs(pctDiff);
  if (abs < 1) return null;

  const better = betterWhenLower ? pctDiff < 0 : pctDiff > 0;
  const label = better
    ? t('benchmark.betterThan', { pct: abs.toFixed(0) })
    : t('benchmark.slowerThan', { pct: abs.toFixed(0) });

  return (
    <span className={`bm-comparison-chip ${better ? 'bm-chip-better' : 'bm-chip-worse'}`}>
      {better ? '▲' : '▼'} {label}
    </span>
  );
}

// ── BenchmarkCard ──────────────────────────────────────────────────────

/**
 * Displays a best-run benchmark for a single task category.
 *
 * Props:
 *   benchmark  — result from calcBenchmark(), or null for empty state
 *   mode       — 'admin' | 'production'
 *   currency   — ISO currency code (admin only)
 *   currentTask — optional task to compare against benchmark
 *   title      — optional override for card header label
 *   compact    — render a smaller inline variant (no basis note, tighter padding)
 */
export default function BenchmarkCard({
  benchmark,
  mode = 'admin',
  currency = 'CAD',
  currentTask = null,
  title = null,
  compact = false,
}) {
  const { t } = useTranslation();

  if (!benchmark) {
    return (
      <div className={`bm-card bm-card-empty${compact ? ' bm-card-compact' : ''}`}>
        <span className="bm-empty-label">{t('benchmark.notEnoughRuns')}</span>
      </div>
    );
  }

  const isAdmin = mode === 'admin';
  const comparison = currentTask ? calcBenchmarkComparison(benchmark, currentTask) : null;

  const workers =
    benchmark.employeeNames?.length > 0
      ? benchmark.employeeNames.length === 1
        ? benchmark.employeeNames[0]
        : `${benchmark.employeeNames[0]} +${benchmark.employeeNames.length - 1}`
      : benchmark.teamSize
      ? t('benchmark.teamX', { size: benchmark.teamSize })
      : null;

  const hasUnits =
    benchmark.quantityCompleted != null && benchmark.quantityCompleted > 0;

  const headline = hasUnits
    ? `${formatDuration(benchmark.durationMinutes)} ${t('benchmark.forQty', { qty: benchmark.quantityCompleted.toLocaleString(), unit: benchmark.unitType || t('benchmark.units') })}`
    : formatDuration(benchmark.durationMinutes);

  const statMetrics = isAdmin
    ? buildAdminMetrics(benchmark, t, currency)
    : buildProductionMetrics(benchmark, t);

  const cardClass = [
    'bm-card',
    compact && 'bm-card-compact',
    !compact && isAdmin && 'bm-card-admin',
    !compact && !isAdmin && 'bm-card-production',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClass}>
      {/* Header */}
      <div className="bm-header">
        <div className="bm-header-main">
          <span className="bm-icon-wrap" aria-hidden="true">
            <Trophy size={14} className="bm-trophy" />
          </span>
          <span className="bm-title">{title || t('benchmark.bestRun')}</span>
        </div>
        <div className="bm-header-meta">
          {benchmark.runCount > 0 && (
            <span className="bm-run-count">{t('benchmark.runs', { count: benchmark.runCount })}</span>
          )}
          {benchmark.date && (
            <span className="bm-date">{formatDate(benchmark.date)}</span>
          )}
        </div>
      </div>

      {/* Headline */}
      <div className="bm-hero">
        <span className="bm-headline">{headline}</span>
      </div>

      {/* Stat tiles */}
      {statMetrics.length > 0 && (
        <div className="bm-stats">
          {statMetrics.map(({ key, label, value }) => (
            <div key={key} className="bm-stat">
              <span className="bm-stat-label">{label}</span>
              <span className="bm-stat-value">{value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Context: worker + comparison chip */}
      {(workers || comparison) && (
        <div className="bm-meta">
          {workers && <span className="bm-worker">{workers}</span>}
          {comparison && <ComparisonChip comparison={comparison} />}
        </div>
      )}

      {/* Production guide note */}
      {!isAdmin && !compact && (
        <p className="bm-basis">{t('benchmark.guideNote')}</p>
      )}

      {/* Compact production note */}
      {!isAdmin && compact && (
        <p className="bm-production-note">{t('benchmark.guideNote')}</p>
      )}

      {/* Basis note (admin, not compact) */}
      {isAdmin && !compact && (
        <p className="bm-basis">{metricBasis(benchmark, t)}</p>
      )}
    </div>
  );
}

// ── BenchmarkEmptyState (explicit empty state for section containers) ──

export function BenchmarkEmptyState() {
  const { t } = useTranslation();
  return (
    <div className="bm-card bm-card-empty">
      <span className="bm-empty-label">{t('benchmark.notEnoughRuns')}</span>
    </div>
  );
}
